import { FC } from "react";
import { VStack } from "@design-system/VStack/VStack";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { useUpdateOrganizationClientMutation } from "@api/organizationClient.api";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import {
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ClientDetailsRow } from "../views/clientViewConfig";
import actionsStyles from "../ClientActions.module.scss";
import styles from "./OrgWorkspaceWarningModal.module.scss";

interface Props {
    client: ClientDetailsRow;
    onClose: () => void;
    onUpgradeSuccess: () => void;
}

export const UpgradePlanToAddEntityModal: FC<Props> = ({
    client,
    onClose,
    onUpgradeSuccess,
}) => {
    const activeOrganization = useActiveOrganization();
    const updateClientMutation = useUpdateOrganizationClientMutation();

    const handleUpdatePlan = async () => {
        if (!activeOrganization || !client.bookkeepingStartDate) {
            return;
        }

        try {
            await updateClientMutation.mutateAsync({
                organizationId: activeOrganization.id,
                clientId: client.id,
                payload: {
                    clientName: client.name,
                    bookkeepingStartDate: client.bookkeepingStartDate,
                    plan: FLAT_RATE_PLANS.PLUS,
                },
            });

            onUpgradeSuccess();
        } catch {
            // error is displayed via updateClientMutation.isError
        }
    };

    return (
        <>
            <StandardModalHeader className={actionsStyles.modalHeader}>
                Upgrade to Plus to add an entity
            </StandardModalHeader>
            <VStack gap={8} className="mt-2">
                <div>
                    Adding an entity requires a Plus plan. Upgrading will take
                    effect immediately and you can add the entity.
                </div>
                {updateClientMutation.isError && (
                    <div className="text-danger">
                        Something went wrong. Please try again.
                    </div>
                )}
            </VStack>
            <StandardModalFooter className={styles.footer}>
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                    size="xl"
                    disabled={updateClientMutation.isPending}
                >
                    Cancel
                </Button>
                <ButtonWithLoader
                    type="button"
                    variant="primary"
                    onClick={handleUpdatePlan}
                    size="xl"
                    disabled={updateClientMutation.isPending}
                    loading={updateClientMutation.isPending}
                >
                    Update plan
                </ButtonWithLoader>
            </StandardModalFooter>
        </>
    );
};
